import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { FeedItem, FeedPost, MyReachStatus } from '@up2d8/contracts';
import type { OutboxEntry } from './outbox-rules';

/**
 * De lokale opslag van de medewerkersapp (Stap 12).
 *
 * In productiehallen, koelcellen en magazijnen is geen dekking. Deze database is wat de app dan nog
 * heeft: de laatste berichten met hun tekst, de eigen standen, en een wachtrij met wat er nog
 * verstuurd moet worden.
 *
 * Twee dingen bepalen de vorm:
 *
 * 1. **Het is een kopie, geen bron.** Alles hier komt van de server en mag zonder verlies weg. Dat
 *    maakt wissen bij een sessie-einde eenvoudig en veilig (ADR-0046) — behalve de wachtrij, en die
 *    is dan ook het enige wat we niet zomaar weggooien zolang de sessie geldig is.
 * 2. **Het staat op het toestel van iemand anders.** Een telefoon raakt kwijt, wordt doorgegeven en
 *    gaat mee naar huis. Daarom: geen namen van collega's, geen bereikcijfers, geen beheerdata —
 *    alleen de berichten die deze medewerker zelf kreeg.
 */

/**
 * De versie van het lokale schema.
 *
 * Hoort bij de versie van de app (`APP_VERSION` hieronder): bij een nieuwe app met een andere vorm
 * van de gegevens wordt de oude database weggegooid in plaats van gemigreerd. Dat mag hier, want het
 * is een kopie — en een migratiepad onderhouden voor een cache is werk dat niemand terugziet.
 */
const DB_VERSION = 1;
const DB_NAME = 'up2d8-offline';

/** Hoeveel berichten er lokaal bewaard blijven. [AANNAME] */
export const MAX_CACHED_POSTS = 50;

/** Van hoeveel berichten de bijlagen bewaard blijven. [AANNAME] */
export const MAX_CACHED_ATTACHMENT_POSTS = 20;

/** De grootste bijlage die we lokaal bewaren, in bytes. [AANNAME] */
export const MAX_CACHED_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** De naam van de Cache Storage waar bijlagen in liggen; de service worker gebruikt dezelfde. */
export const ATTACHMENT_CACHE = 'up2d8-attachments';

export interface CachedPost {
  postId: string;
  /** Het bericht zoals het op het scherm stond, inclusief de taal waarin het getoond werd. */
  post: FeedPost;
  /** De kaart in de feed; apart bewaard omdat de lijst ook offline moet werken. */
  item: FeedItem | null;
  cachedAt: string;
}

interface Up2d8Db extends DBSchema {
  posts: { key: string; value: CachedPost; indexes: { 'by-cached-at': string } };
  postStatus: { key: string; value: MyReachStatus };
  outbox: { key: string; value: OutboxEntry };
  meta: { key: string; value: unknown };
}

let handle: Promise<IDBPDatabase<Up2d8Db>> | null = null;

export function offlineDb(): Promise<IDBPDatabase<Up2d8Db>> {
  handle ??= openDB<Up2d8Db>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // Bij een sprong in de versie: alles weg en opnieuw opbouwen. Zie de opmerking bij
      // DB_VERSION — dit is een kopie en geen bron.
      if (oldVersion > 0) {
        for (const name of [...db.objectStoreNames]) db.deleteObjectStore(name);
      }

      const posts = db.createObjectStore('posts', { keyPath: 'postId' });
      posts.createIndex('by-cached-at', 'cachedAt');
      db.createObjectStore('postStatus', { keyPath: 'postId' });
      db.createObjectStore('outbox', { keyPath: 'id' });
      db.createObjectStore('meta');
    },
  });

  return handle;
}

// --- Berichten ------------------------------------------------------------------------

export async function savePost(post: FeedPost, item?: FeedItem | null): Promise<void> {
  const db = await offlineDb();
  const existing = await db.get('posts', post.postId);

  await db.put('posts', {
    postId: post.postId,
    post,
    item: item ?? existing?.item ?? null,
    cachedAt: new Date().toISOString(),
  });

  await prunePosts();
}

export async function saveFeedItems(items: FeedItem[]): Promise<void> {
  const db = await offlineDb();
  const tx = db.transaction('posts', 'readwrite');

  for (const item of items) {
    const existing = await tx.store.get(item.postId);
    await tx.store.put({
      postId: item.postId,
      // Zonder het bericht zelf: dat komt zodra iemand het opent. De kaart is genoeg om de feed
      // offline te kunnen tonen, en dat is waar iemand begint.
      post: existing?.post ?? (null as unknown as FeedPost),
      item,
      cachedAt: existing?.cachedAt ?? new Date().toISOString(),
    });
  }

  await tx.done;
  await prunePosts();
}

export async function cachedFeed(): Promise<FeedItem[]> {
  const db = await offlineDb();
  const all = await db.getAll('posts');

  return all
    .map((row) => row.item)
    .filter((item): item is FeedItem => item !== null)
    .sort((left, right) => right.publishedAt.localeCompare(left.publishedAt));
}

export async function cachedPost(postId: string): Promise<FeedPost | null> {
  const db = await offlineDb();
  return (await db.get('posts', postId))?.post ?? null;
}

export async function removePost(postId: string): Promise<void> {
  const db = await offlineDb();
  await db.delete('posts', postId);
  await db.delete('postStatus', postId);
}

/**
 * Houdt de vijftig nieuwste berichten en gooit de rest weg, met hun bijlagen.
 *
 * Een grens is nodig: een medewerker die twee jaar in dienst is, heeft honderden berichten gehad, en
 * de opslag van een browser is niet onbeperkt (op iOS zeker niet). Vijftig is ruim voor "wat er deze
 * weken speelde", en dat is waar een feed over gaat.
 */
async function prunePosts(): Promise<void> {
  const db = await offlineDb();
  const all = await db.getAll('posts');
  if (all.length <= MAX_CACHED_POSTS) return;

  const teVeel = all
    .sort((left, right) => right.cachedAt.localeCompare(left.cachedAt))
    .slice(MAX_CACHED_POSTS);

  for (const row of teVeel) await removePost(row.postId);
}

// --- Standen --------------------------------------------------------------------------

export async function saveStatuses(statuses: MyReachStatus[]): Promise<void> {
  const db = await offlineDb();
  const tx = db.transaction('postStatus', 'readwrite');
  for (const status of statuses) await tx.store.put(status);
  await tx.done;
}

export async function cachedStatus(postId: string): Promise<MyReachStatus | null> {
  const db = await offlineDb();
  return (await db.get('postStatus', postId)) ?? null;
}

export async function cachedStatuses(): Promise<MyReachStatus[]> {
  const db = await offlineDb();
  return db.getAll('postStatus');
}

// --- Wachtrij -------------------------------------------------------------------------

export async function queueEntry(entry: OutboxEntry): Promise<void> {
  const db = await offlineDb();
  // `add` zou gooien bij een dubbele sleutel; `put` is hier juist gewenst, want dezelfde sleutel
  // betekent dezelfde handeling (zie `outboxKeyOf`).
  await db.put('outbox', entry);
}

export async function queuedEntries(): Promise<OutboxEntry[]> {
  const db = await offlineDb();
  return db.getAll('outbox');
}

export async function removeQueued(id: string): Promise<void> {
  const db = await offlineDb();
  await db.delete('outbox', id);
}

export async function updateQueued(entry: OutboxEntry): Promise<void> {
  const db = await offlineDb();
  await db.put('outbox', entry);
}

// --- Meta -----------------------------------------------------------------------------

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await offlineDb();
  await db.put('meta', value, key);
}

export async function getMeta<T>(key: string): Promise<T | null> {
  const db = await offlineDb();
  return ((await db.get('meta', key)) as T | undefined) ?? null;
}

/** Wanneer de app voor het laatst met de server sprak. Staat op het scherm: "bijgewerkt om …". */
export const LAST_SYNC_KEY = 'lastSyncedAt';

// --- Wissen ---------------------------------------------------------------------------

/**
 * Wist alles: de database én de bestanden in Cache Storage (ADR-0046).
 *
 * Wordt aangeroepen bij uitloggen, bij een sessie die is ingetrokken, en bij een `401` die niet meer
 * te vernieuwen is. Dat is geen nette opruiming maar een eis: een medewerker die uit dienst gaat,
 * levert zijn toestel in of houdt het, en in beide gevallen horen er geen berichten van zijn oude
 * werkgever op te staan.
 *
 * De precache van de app zelf blijft staan (`up2d8-app-…`): dat is onze eigen code, en die wissen zou
 * betekenen dat het aanmeldscherm offline niet meer opent.
 */
export async function clearOfflineData(): Promise<void> {
  handle = null;

  try {
    const db = await offlineDb();
    const tx = db.transaction(['posts', 'postStatus', 'outbox', 'meta'], 'readwrite');
    await Promise.all([
      tx.objectStore('posts').clear(),
      tx.objectStore('postStatus').clear(),
      tx.objectStore('outbox').clear(),
      tx.objectStore('meta').clear(),
    ]);
    await tx.done;
    db.close();
    handle = null;
  } catch {
    // Een browser die IndexedDB blokkeert (privémodus) heeft niets om te wissen.
  }

  try {
    if ('caches' in globalThis) {
      const names = await caches.keys();
      // Alleen de inhoud, niet de app zelf: zonder de precache opent het aanmeldscherm offline niet.
      await Promise.all(
        names.filter((name) => !name.startsWith('up2d8-app')).map((name) => caches.delete(name)),
      );
    }
  } catch {
    // Idem: geen Cache Storage betekent niets om te wissen.
  }
}
